import type { CommentBody } from "@liveblocks/core";

/**
 * Walk a Liveblocks `CommentBody` and extract the set of user ids that were
 * @-mentioned inline (e.g. "user-123"). Deduped, order preserved.
 */
export const extractMentionedUserIds = (body: CommentBody | undefined | null): string[] => {
  if (!body?.content?.length) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  body.content.forEach((block: any) => {
    if (block?.type !== "paragraph") return;
    (block.children || []).forEach((inline: any) => {
      if (inline?.type === "mention" && typeof inline.id === "string") {
        if (seen.has(inline.id)) return;
        seen.add(inline.id);
        ids.push(inline.id);
      }
    });
  });
  return ids;
};

/**
 * Flatten a `CommentBody` into a single plain-text string, preserving
 * `@name` for mentions when a resolver is available, otherwise `@user-id`.
 * Used for the email body preview and subject.
 */
export const flattenCommentBody = (
  body: CommentBody | undefined | null,
  resolveMention?: (userId: string) => string | undefined
): string => {
  if (!body?.content?.length) return "";
  const parts: string[] = [];
  body.content.forEach((block: any, blockIdx: number) => {
    if (block?.type !== "paragraph") return;
    (block.children || []).forEach((inline: any) => {
      if (!inline) return;
      if ("text" in inline) parts.push(String(inline.text || ""));
      else if (inline.type === "mention" && inline.id) {
        const name = resolveMention ? resolveMention(inline.id) : undefined;
        parts.push(`@${name || inline.id}`);
      } else if (inline.type === "link") parts.push(String(inline.text || inline.url || ""));
    });
    if (blockIdx < body.content.length - 1) parts.push("\n");
  });
  return parts.join("").trim();
};
